/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { ReactNode } from 'react'

import Grid from '@mui/material/Grid'

import laurelLogo from 'assets/images/laurel-logo.png'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { useMaterialUIController } from 'context/materialUI'
import PageLayout from 'modules/LayoutContainers/PageLayout'

// Declaring props types for IllustrationLayout
interface Props {
	header?: ReactNode
	title?: string
	description?: string
	children: ReactNode
	illustration?: string
}

function IllustrationLayout({
	header,
	title,
	description,
	illustration,
	children,
}: Props): JSX.Element {
	const [controller] = useMaterialUIController()
	const { darkMode, orgAssets } = controller

	return (
		<PageLayout background="white">
			<Grid
				container
				sx={{
					backgroundColor: ({ palette: { background, white } }) =>
						darkMode ? background.default : white.main,
				}}
			>
				<Grid
					item
					xs={false}
					lg={6}
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '100vh',
						p: 3, // optional padding inside the grid
					}}
				>
					<MDBox
						sx={{
							width: '100%',
							height: '100%',
							borderRadius: '1.5rem 1.5rem 1.5rem 1.5rem', // rounded only on left side
							backgroundImage: `url(${illustration})`,
							backgroundRepeat: 'no-repeat',
							backgroundSize: 'cover',
							backgroundPosition: '65% center',
						}}
					/>
				</Grid>

				<Grid item xs={11} sm={8} md={6} lg={4} xl={3} sx={{ mx: 'auto' }}>
					<MDBox display="flex" flexDirection="column" justifyContent="center" height="75vh">
						<MDBox
							display={'flex'}
							flexDirection={'row'}
							justifyContent={'center'}
							alignItems={'center'}
							gap={2}
							my={10}
						>
							<MDBox
								component="img"
								src={orgAssets?.logo?.light || laurelLogo}
								alt="Brand"
								width="15vw"
							/>
						</MDBox>
						<MDBox py={3} px={3} textAlign="center">
							{!header ? (
								<>
									<MDBox mb={1} textAlign="center">
										<MDTypography variant="h4" fontWeight="bold">
											{title}
										</MDTypography>
									</MDBox>
									<MDTypography variant="body2" color="text">
										{description}
									</MDTypography>
								</>
							) : (
								header
							)}
						</MDBox>
						<MDBox p={3}>{children}</MDBox>
					</MDBox>
				</Grid>
			</Grid>
		</PageLayout>
	)
}

// Declaring default props for IllustrationLayout
IllustrationLayout.defaultProps = {
	header: '',
	title: '',
	description: '',
	illustration: '',
}

export default IllustrationLayout
